import React from 'react';
import { InlineSpinner } from '../../common/loading';

import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  alpha,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { SEARCE_COLORS } from '../../../theme';
import { ProjectDropdownOption, TeamMemberOption } from '../../../services/api';
import { EscalatedBySource, FormData, LevelType } from './types';
import { getProjectStatusColor, filterProjectsByStatus } from './utils';

interface EscalationFormProps {
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string | number | null | File) => void;
  selectedLevel: LevelType;
  isEscalationResolved: boolean;
  onEscalationResolvedChange: (resolved: boolean) => void;
  projectOptions: ProjectDropdownOption[];
  projectsLoading: boolean;
  projectStatusFilter: string | null;
  onProjectStatusFilterChange: (filter: string | null) => void;
  selectedProject: ProjectDropdownOption | null;
  onProjectSelect: (project: ProjectDropdownOption | null) => void;
  teamMemberOptions: TeamMemberOption[];
  teamMembersLoading: boolean;
  selectedTeamMembers: TeamMemberOption[];
  onTeamMemberSelect: (members: TeamMemberOption[]) => void;
  isFormDisabled: boolean;
}

export const EscalationForm: React.FC<EscalationFormProps> = ({
  formData,
  onInputChange,
  selectedLevel,
  isEscalationResolved,
  onEscalationResolvedChange,
  projectOptions,
  projectsLoading,
  projectStatusFilter,
  onProjectStatusFilterChange,
  selectedProject,
  onProjectSelect,
  teamMemberOptions,
  teamMembersLoading,
  selectedTeamMembers,
  onTeamMemberSelect,
  isFormDisabled,
}) => {
  const filteredProjectOptions = filterProjectsByStatus(projectOptions, projectStatusFilter);
  const isProjectLevel = selectedLevel === 'project';
  const escalatedBySource = formData.escalatedBySource;
  const showEscalatedByPersonFields =
    isProjectLevel ? Boolean(escalatedBySource) : true;
  const nameLabel =
    isProjectLevel && escalatedBySource === 'client'
      ? 'Client Name'
      : 'Escalated by (Name)';
  const designationLabel =
    isProjectLevel && escalatedBySource === 'client'
      ? 'Client Designation'
      : 'Escalated by (Designation)';

  const handleEscalatedBySourceChange = (value: EscalatedBySource) => {
    onInputChange('escalatedBySource', value);
    onInputChange('escalatedByName', '');
    onInputChange('escalatedByDesignation', '');
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isFormDisabled ? alpha(SEARCE_COLORS.neutral_200, 0.5) : SEARCE_COLORS.cardBackground,
      transition: 'background-color 0.2s ease',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isFormDisabled ? SEARCE_COLORS.border : SEARCE_COLORS.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: SEARCE_COLORS.primary,
      },
      '&.Mui-disabled': {
        backgroundColor: alpha(SEARCE_COLORS.neutral_200, 0.3),
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: SEARCE_COLORS.primary,
    },
  };

  return (
    <Grid container spacing={3}>
      {/* Email - Auto-filled from logged-in user */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email"
          required
          type="email"
          value={formData.email}
          disabled
          helperText="Your logged-in email address"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {/* Project Code/Opportunity ID - Dynamic Dropdown */}
      <Grid item xs={12}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Filter by project status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {['All', 'Closed', 'In progress', 'Yet to Start'].map((status) => {
              const isAll = status === 'All';
              const isSelected = isAll ? !projectStatusFilter : projectStatusFilter === status;
              return (
                <Chip
                  key={status}
                  label={status}
                  size="small"
                  onClick={() => onProjectStatusFilterChange(isAll ? null : (projectStatusFilter === status ? null : status))}
                  sx={{
                    backgroundColor: isSelected ? alpha(SEARCE_COLORS.primary, 0.15) : undefined,
                    color: isSelected ? SEARCE_COLORS.primary : undefined,
                    borderColor: isSelected ? SEARCE_COLORS.primary : SEARCE_COLORS.border,
                    border: '1px solid',
                    '&:hover': {
                      backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
        <Autocomplete
          options={filteredProjectOptions}
          getOptionLabel={(option) => option.display_label}
          value={selectedProject}
          onChange={(_, newValue) => onProjectSelect(newValue)}
          loading={projectsLoading}
          isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Code / Opportunity ID / Event"
              required
              helperText="Select from your projects (where you are the owner)"
              sx={textFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {projectsLoading ? <InlineSpinner size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const statusColor = getProjectStatusColor(option.status);
            const statusLabel = option.status || 'Unknown';
            return (
              <li {...props} key={option.project_code}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {option.project_code}
                      </Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ color: statusColor, fontWeight: 500 }}>
                          {statusLabel}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {option.project_name} {option.account ? `• ${option.account}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }}
          noOptionsText={projectsLoading ? "Loading projects..." : "No projects found where you are the owner"}
        />
      </Grid>

      {isProjectLevel && (
        <Grid item xs={12}>
          <FormControl component="fieldset" required>
            <FormLabel
              component="legend"
              sx={{ color: SEARCE_COLORS.textPrimary, fontWeight: 600, mb: 0.5 }}
            >
              Escalated by
            </FormLabel>
            <RadioGroup
              row
              value={escalatedBySource}
              onChange={(e) => handleEscalatedBySourceChange(e.target.value as EscalatedBySource)}
            >
              <FormControlLabel
                value="client"
                control={
                  <Radio
                    sx={{
                      color: SEARCE_COLORS.primary,
                      '&.Mui-checked': { color: SEARCE_COLORS.primary },
                    }}
                  />
                }
                label="Client"
              />
              <FormControlLabel
                value="searce"
                control={
                  <Radio
                    sx={{
                      color: SEARCE_COLORS.primary,
                      '&.Mui-checked': { color: SEARCE_COLORS.primary },
                    }}
                  />
                }
                label="Searce"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}

      {showEscalatedByPersonFields && (
        <>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={nameLabel}
              required
              value={formData.escalatedByName}
              onChange={(e) => onInputChange('escalatedByName', e.target.value)}
              placeholder="Your answer"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={designationLabel}
              required
              value={formData.escalatedByDesignation}
              onChange={(e) => onInputChange('escalatedByDesignation', e.target.value)}
              placeholder="Your answer"
              sx={textFieldSx}
            />
          </Grid>
        </>
      )}

      {/* Project Owner Name - Auto-filled from selected project */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Project Owner Name *"
          value={formData.projectOwnerName}
          disabled
          helperText="Auto-filled from the selected project"
          sx={{
            ...textFieldSx,
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: SEARCE_COLORS.textPrimary,
              opacity: 0.8,
            },
          }}
        />
      </Grid>

      {/* Project Member Name - Multi-select for individual level */}
      {selectedLevel === 'individual' && (
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={teamMemberOptions}
            getOptionLabel={(option) => option.email}
            value={selectedTeamMembers}
            onChange={(_, newValue) => onTeamMemberSelect(newValue)}
            loading={teamMembersLoading}
            disabled={!selectedProject}
            isOptionEqualToValue={(option, value) => option.email === value.email}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Project Member Name *"
                helperText={
                  !selectedProject 
                    ? "Please select a project first" 
                    : "Select team members assigned to this project"
                }
                sx={textFieldSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {teamMembersLoading ? <InlineSpinner size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.email}>
                <Typography variant="body2">{option.email}</Typography>
              </li>
            )}
            noOptionsText={
              teamMembersLoading 
                ? "Loading team members..." 
                : !selectedProject 
                  ? "Select a project first" 
                  : "No team members found for this project"
            }
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Issue Description"
          required
          multiline
          rows={4}
          value={formData.issueDescription}
          onChange={(e) => onInputChange('issueDescription', e.target.value)}
          placeholder="Your answer"
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          select
          label="Area of Escalation"
          required
          value={formData.areaOfEscalation}
          onChange={(e) => {
            const value = e.target.value;
            onInputChange('areaOfEscalation', value);
            if (value !== 'Other') {
              onInputChange('areaOfEscalationOther', '');
            }
          }}
          sx={textFieldSx}
        >
          {['Technical Skills', 'Behavioral Skills', 'Communication Skills', 'Other'].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {formData.areaOfEscalation === 'Other' && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Area of Escalation (Other)"
            required
            value={formData.areaOfEscalationOther}
            onChange={(e) => onInputChange('areaOfEscalationOther', e.target.value)}
            placeholder="Your answer"
            sx={textFieldSx}
          />
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Date raised"
          required
          type="date"
          value={formData.dateRaised}
          onChange={(e) => onInputChange('dateRaised', e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: new Date().toLocaleDateString('en-CA') }}
          helperText="Cannot be a future date"
          sx={textFieldSx}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isEscalationResolved}
              onChange={(e) => {
                const checked = e.target.checked;
                onEscalationResolvedChange(checked);
                if (!checked) {
                  onInputChange('dateResolved', '');
                  onInputChange('solutionProposed', '');
                  onInputChange('rootCause', '');
                  onInputChange('learnings', '');
                }
              }}
              sx={{ color: SEARCE_COLORS.primary, '&.Mui-checked': { color: SEARCE_COLORS.primary } }}
            />
          }
          label="Is the escalation resolved?"
          sx={{ color: SEARCE_COLORS.textPrimary }}
        />
      </Grid>

      {isEscalationResolved && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date resolved"
              required
              type="date"
              value={formData.dateResolved}
              onChange={(e) => onInputChange('dateResolved', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: formData.dateRaised || new Date().toLocaleDateString('en-CA'),
                max: new Date().toLocaleDateString('en-CA'),
              }}
              helperText="Must be on or after date raised; cannot be a future date"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Solution Proposed"
              required
              multiline
              rows={4}
              value={formData.solutionProposed}
              onChange={(e) => onInputChange('solutionProposed', e.target.value)}
              placeholder="Your answer"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Root Cause"
              required
              multiline
              rows={4}
              value={formData.rootCause}
              onChange={(e) => onInputChange('rootCause', e.target.value)}
              placeholder="Your answer"
              sx={textFieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Learnings"
              required
              multiline
              rows={4}
              value={formData.learnings}
              onChange={(e) => onInputChange('learnings', e.target.value)}
              placeholder="Your answer"
              sx={textFieldSx}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
